import { useState, useEffect, useRef } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { fileToDataUrl } from "../../lib/upload";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Pencil, ImagePlus, Truck, DollarSign } from "lucide-react";
import { LevelBadge, CREW_LEVEL_OPTIONS } from "../../components/shared/LevelBadge";

const CONFIG = {
  employees: {
    title: "Crew", statusOptions: ["available", "on_mission", "off_duty"],
    fields: [["name", "Name"], ["crew_level", "Level"], ["role", "Role"], ["phone", "Phone"], ["email", "Email"]],
    cols: (r) => [r.role, r.phone], hasPhoto: true,
  },
  vehicles: {
    title: "Vehicles", statusOptions: ["ready", "in_service", "maintenance"],
    fields: [["name", "Name"], ["type", "Type"], ["plate", "Plate"], ["capacity", "Capacity"]],
    cols: (r) => [r.type, r.plate], hasPhoto: true,
  },
  equipment: {
    title: "Equipment", statusOptions: null,
    fields: [["name", "Name"], ["category", "Category"], ["quantity", "Quantity"]],
    cols: (r) => [r.category, `Qty: ${r.quantity}`], hasPhoto: false,
  },
};

const Avatar = ({ r, size = 12 }) =>
  r.photo ? (
    <img src={r.photo} alt={r.name} className={`w-${size} h-${size} rounded-full object-cover border shrink-0`} style={{ width: size * 4, height: size * 4 }} />
  ) : (
    <div className="rounded-full bg-[var(--cc-accent)]/15 text-[var(--cc-accent)] flex items-center justify-center font-heading font-bold shrink-0" style={{ width: size * 4, height: size * 4 }}>
      {r.name?.[0]}
    </div>
  );

export default function Resources() {
  const { user } = useAuth();
  const isDispatcher = user?.role === "dispatcher";
  const [tab, setTab] = useState("employees");
  const [data, setData] = useState({ employees: [], vehicles: [], equipment: [] });
  const [payroll, setPayroll] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [accountFor, setAccountFor] = useState(null);
  const [account, setAccount] = useState({ email: "", password: "" });
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", note: "" });
  const fileRef = useRef(null);

  const load = () => {
    Promise.all(["employees", "vehicles", "equipment"].map((k) => api.get(`/resources/${k}`).then((r) => [k, r.data])))
      .then((entries) => setData(Object.fromEntries(entries)))
      .catch(() => {});
    if (!isDispatcher) api.get("/payroll").then((r) => setPayroll(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const cfg = CONFIG[tab];
  const paidTotals = payroll.reduce((acc, p) => ({ ...acc, [p.employee_id]: (acc[p.employee_id] || 0) + p.amount }), {});

  const openCreate = () => { setEditing(null); setForm(tab === "employees" ? { crew_level: "S1" } : {}); setPhoto(null); setFormOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm(Object.fromEntries(cfg.fields.map(([k]) => [k, r[k] ?? ""])));
    setPhoto(r.photo || null);
    setFormOpen(true);
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPhoto(await fileToDataUrl(file, 900));
    } catch { toast.error("Could not read image"); }
    e.target.value = "";
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.quantity !== undefined && payload.quantity !== "") payload.quantity = Number(payload.quantity);
      if (cfg.hasPhoto && photo !== null) payload.photo = photo;
      if (editing) {
        await api.patch(`/resources/${tab}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/resources/${tab}`, payload);
        toast.success("Added");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setStatus = async (rid, status) => {
    await api.patch(`/resources/${tab}/${rid}`, { status });
    load();
  };

  const remove = async (rid) => {
    await api.delete(`/resources/${tab}/${rid}`);
    toast.success("Removed");
    load();
  };

  const createAccount = async () => {
    try {
      await api.post(`/resources/employees/${accountFor.id}/account`, account);
      toast.success(`Crew portal login created for ${accountFor.name}`);
      setAccountFor(null);
      setAccount({ email: "", password: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitPay = async () => {
    try {
      await api.post("/payroll", { employee_id: payFor.id, amount: Number(pay.amount), note: pay.note });
      toast.success(`${fmtMoney(Number(pay.amount))} paid to ${payFor.name}`);
      setPay({ amount: "", note: "" });
      setPayFor(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="ops-resources-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Assets</p>
          <h1 className="page-title mt-1.5">Resources</h1>
        </div>
        <Button data-testid="resources-add-button" onClick={openCreate} className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Add {tab === "employees" ? "Crew" : cfg.title.slice(0, -1)}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto rounded-full">
          {Object.keys(CONFIG).map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`resources-tab-${k}`} className="rounded-full capitalize">
              {k === "employees" ? "Crew" : k}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="employees">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            {data.employees.map((r) => (
              <div key={r.id} data-testid={`resource-card-${r.id}`} className="rounded-xl border bg-card p-5 card-lift">
                <div className="flex items-start gap-4">
                  <Avatar r={r} size={12} />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold truncate">{r.name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <LevelBadge level={r.crew_level || "S1"} size="xs" />
                      <p className="text-xs text-muted-foreground truncate">{[r.role, r.phone].filter(Boolean).join(" · ")}</p>
                    </div>
                    <p className="font-mono-cc text-[10px] text-muted-foreground mt-1.5 uppercase tracking-wider">
                      {isDispatcher ? (
                        <span>{r.login_email ? "Portal login active" : "No portal login"}</span>
                      ) : (
                        <>Paid to date: <span className="text-[var(--cc-accent)]">{fmtMoney(paidTotals[r.id] || 0)}</span></>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`resource-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`resource-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-red-500" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <StatusBadge status={r.status} />
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid={`resource-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONFIG.employees.statusOptions.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!isDispatcher && (
                  <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2">
                    <Button data-testid={`resource-pay-${r.id}`} size="sm" className="rounded-full text-xs h-7 btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold"
                      onClick={() => { setPayFor(r); setPay({ amount: "", note: "" }); }}>
                      <DollarSign className="w-3 h-3 mr-1" /> Pay
                    </Button>
                    {r.login_email ? (
                      <p className="text-[11px] text-muted-foreground font-mono-cc flex items-center gap-1.5">
                        <KeyRound className="w-3 h-3 text-[var(--cc-accent)]" /> {r.login_email}
                      </p>
                    ) : (
                      <Button data-testid={`resource-create-login-${r.id}`} variant="outline" size="sm" className="rounded-full text-xs h-7"
                        onClick={() => { setAccountFor(r); setAccount({ email: "", password: "" }); }}>
                        <KeyRound className="w-3 h-3 mr-1.5" /> Crew Login
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="vehicles">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            {data.vehicles.map((r) => (
              <div key={r.id} data-testid={`resource-card-${r.id}`} className="rounded-xl border bg-card overflow-hidden card-lift">
                {r.photo ? (
                  <img src={r.photo} alt={r.name} className="w-full h-40 object-cover" data-testid={`vehicle-photo-${r.id}`} />
                ) : (
                  <div className="w-full h-40 bg-secondary/60 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Truck className="w-8 h-8 opacity-40" />
                    <p className="font-mono-cc text-[10px] uppercase tracking-widest opacity-60">No photo yet</p>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-heading font-semibold truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{[r.type, r.plate, r.capacity].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`resource-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`resource-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-red-500" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <StatusBadge status={r.status} />
                    <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid={`resource-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONFIG.vehicles.statusOptions.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="equipment">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            {data.equipment.map((r) => (
              <div key={r.id} data-testid={`resource-card-${r.id}`} className="rounded-xl border bg-card p-5 card-lift">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{CONFIG.equipment.cols(r).filter(Boolean).join(" · ")}</p>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`resource-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`resource-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-red-500" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="resource-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : `Add ${cfg.title.slice(0, -1)}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {cfg.hasPhoto && (
              <div className="flex items-center gap-4">
                {photo ? (
                  tab === "vehicles"
                    ? <img src={photo} alt="preview" className="w-28 h-20 rounded-lg object-cover border" />
                    : <img src={photo} alt="preview" className="w-16 h-16 rounded-full object-cover border" />
                ) : (
                  <div className={`${tab === "vehicles" ? "w-28 h-20 rounded-lg" : "w-16 h-16 rounded-full"} bg-secondary flex items-center justify-center text-muted-foreground border`}>
                    <ImagePlus className="w-5 h-5" />
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="resource-photo-input" />
                <div className="space-x-2">
                  <Button data-testid="resource-photo-button" type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()}>
                    <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> {photo ? "Change Photo" : "Add Photo"}
                  </Button>
                  {photo && <Button type="button" variant="ghost" size="sm" className="rounded-full text-red-500" onClick={() => setPhoto("")}>Remove</Button>}
                </div>
              </div>
            )}
            {cfg.fields.map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <Label>{label}</Label>
                {key === "crew_level" ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      {CREW_LEVEL_OPTIONS.map((o) => (
                        <button key={o.id} type="button" data-testid={`resource-form-level-${o.id}`}
                          onClick={() => setForm({ ...form, crew_level: o.id })}
                          className={`rounded-2xl border-2 py-3 flex flex-col items-center gap-1.5 transition-all ${
                            form.crew_level === o.id ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/5" : "border-border hover:border-foreground/25"}`}>
                          <LevelBadge level={o.id} size="sm" showLabel={false} />
                          <span className="text-[10px] text-muted-foreground">{o.label.split("·")[1]?.trim()}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Input data-testid={`resource-form-${key}`} type={key === "quantity" ? "number" : "text"} value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                )}
              </div>
            ))}
            <Button data-testid="resource-form-submit" onClick={save} disabled={!form.name} className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
              {editing ? "Save Changes" : "Add"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent data-testid="payroll-dialog">
          {payFor && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Pay {payFor.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3"><p className="overline-cc">Paid to date</p><p className="mt-1 font-semibold">{fmtMoney(paidTotals[payFor.id] || 0)}</p></div>
                  <div className="rounded-lg border p-3"><p className="overline-cc">Role</p><p className="mt-1 font-semibold">{payFor.role}</p></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Amount ($)</Label>
                  <Input data-testid="payroll-amount-input" type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="850" />
                </div>
                <div className="space-y-1.5">
                  <Label>Note (optional)</Label>
                  <Input data-testid="payroll-note-input" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="Week of Aug 4–8" />
                </div>
                <Button data-testid="payroll-submit" onClick={submitPay} disabled={!pay.amount || Number(pay.amount) <= 0}
                  className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                  <DollarSign className="w-4 h-4 mr-1" /> Record Payment
                </Button>
                <div>
                  <p className="overline-cc mb-2">Payment History</p>
                  <div className="rounded-lg border divide-y max-h-44 overflow-y-auto">
                    {payroll.filter((p) => p.employee_id === payFor.id).map((p) => (
                      <div key={p.id} className="px-4 py-2.5 flex items-center justify-between text-sm" data-testid={`payroll-record-${p.id}`}>
                        <div>
                          <p className="font-semibold">{fmtMoney(p.amount)}</p>
                          {p.note && <p className="text-xs text-muted-foreground">{p.note}</p>}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono-cc">{fmtDate(p.created_at)}</p>
                      </div>
                    ))}
                    {payroll.filter((p) => p.employee_id === payFor.id).length === 0 && (
                      <p className="px-4 py-4 text-xs text-muted-foreground">No payments recorded yet.</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Crew login dialog */}
      <Dialog open={!!accountFor} onOpenChange={(o) => !o && setAccountFor(null)}>
        <DialogContent data-testid="resource-account-dialog">
          {accountFor && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Crew login for {accountFor.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">They'll sign in at /login and land in the Crew Portal to see assigned missions, update checklists, and mark jobs complete.</p>
                <div className="space-y-1.5">
                  <Label>Login email</Label>
                  <Input data-testid="account-email-input" type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} placeholder="crew@castle-corp.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Password</Label>
                  <Input data-testid="account-password-input" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} placeholder="Min 6 characters" />
                </div>
                <Button data-testid="account-create-submit" onClick={createAccount} disabled={!account.email || account.password.length < 6}
                  className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                  Create Login
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
